import React, { useCallback, useEffect, useState } from 'react';
import { ScanLine, Package, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';
import API_BASE from '../services/apiBase';
import { useAuth } from '../features/auth/AuthContext';
import usePackageCameraScan from '../hooks/usePackageCameraScan';
import { TRACKING_STATUS_OPTIONS } from '../utils/packageTrack';
import { validateScanForm } from '../utils/packageValidation';
import '../styles/package-tracking.css';

const SCAN_STATUSES = TRACKING_STATUS_OPTIONS;

function nowDateInput() {
  return new Date().toISOString().slice(0, 10);
}

function nowTimeInput() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function AdminPackageScan() {
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('malaysia-warehouse');
  const [eventDate, setEventDate] = useState(nowDateInput);
  const [eventTime, setEventTime] = useState(nowTimeInput);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [lastPkg, setLastPkg] = useState(null);
  const [autoUpdateOnScan, setAutoUpdateOnScan] = useState(true);
  const [cameraOn, setCameraOn] = useState(false);

  const submitScan = useCallback(async (codeOverride) => {
    const c = (codeOverride ?? code).trim().toUpperCase();
    const validation = validateScanForm({
      code: c,
      status,
      eventDate,
      eventTime,
    });
    if (!validation.ok) {
      setMsg(validation.message);
      setLastPkg(null);
      return;
    }
    if (!user?.token) return;
    setBusy(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/packages/${encodeURIComponent(validation.code)}/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          status,
          eventDate,
          eventTime,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Scan failed');
      setLastPkg(json.data);
      setMsg(`Updated: ${json.data.packageCode} → ${json.data.currentStatus}`);
      setCode('');
    } catch (err) {
      setMsg(err.message || 'Scan failed');
      setLastPkg(null);
    } finally {
      setBusy(false);
    }
  }, [code, user, status, eventDate, eventTime]);

  const handleDetected = useCallback((parsed) => {
    setCode(parsed);
    setCameraOn(false);
    setMsg(`Scanned: ${parsed}`);
    if (autoUpdateOnScan) {
      submitScan(parsed);
    }
  }, [autoUpdateOnScan, submitScan]);

  const { videoRef, cameraError } = usePackageCameraScan({
    onDetect: handleDetected,
    active: cameraOn,
  });

  useEffect(() => {
    if (cameraError) setCameraOn(false);
  }, [cameraError]);

  const handleScan = (e) => {
    e.preventDefault();
    submitScan();
  };

  if (!user?.token || (user.role !== 'admin' && user.role !== 'staff')) {
    return (
      <div className="pkg-scan-page">
        <p>Please <Link to="/admin/login">sign in as admin</Link> to scan packages.</p>
      </div>
    );
  }

  return (
    <div className="pkg-scan-page">
      <div className="pkg-scan-card pkg-scan-card--wide">
        <div className="pkg-scan-head">
          <ScanLine size={28} />
          <h1>Package scan</h1>
          <p>Scan a sticker QR or barcode to update package tracking.</p>
        </div>

        <div className="pkg-scan-help">
          <strong>How to use:</strong>
          <ol>
            <li>Select status, date and time (use past date/time if updating late).</li>
            <li>Tap the camera icon next to Package code to scan QR or barcode.</li>
            <li>USB barcode scanner works in the Package code field (types like a keyboard).</li>
            <li>Or type the code manually and click <strong>Update tracking</strong>.</li>
          </ol>
        </div>

        <label className="pkg-scan-auto pkg-scan-auto--solo">
          <input
            type="checkbox"
            checked={autoUpdateOnScan}
            onChange={(e) => setAutoUpdateOnScan(e.target.checked)}
          />
          Auto-update on scan
        </label>

        <div className={`pkg-scan-camera-wrap pkg-scan-camera-wrap--inline${cameraOn ? '' : ' pkg-scan-camera-wrap--off'}`}>
          <video ref={videoRef} className="pkg-scan-video" muted playsInline />
          {cameraOn ? (
            <p className="pkg-scan-camera-hint">Hold the sticker QR or barcode within the frame</p>
          ) : null}
        </div>
        {cameraError ? <p className="pkg-scan-msg err">{cameraError}</p> : null}

        <form onSubmit={handleScan} className="pkg-scan-form">
          <label>
            Package code
            <div className="pkg-scan-code-row">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="COL-01818-01"
                autoComplete="off"
              />
              <button
                type="button"
                className={`pkg-scan-code-camera${cameraOn ? ' pkg-scan-code-camera--on' : ''}`}
                onClick={() => setCameraOn((v) => !v)}
                aria-label={cameraOn ? 'Turn camera off' : 'Scan with camera'}
                title={cameraOn ? 'Camera off' : 'Scan QR / barcode'}
              >
                <Camera size={20} aria-hidden />
              </button>
            </div>
          </label>

          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {SCAN_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>

          <div className="pkg-scan-datetime">
            <label>
              Date
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                required
              />
            </label>
            <label>
              Time
              <input
                type="time"
                value={eventTime}
                onChange={(e) => setEventTime(e.target.value)}
                required
              />
            </label>
          </div>

          <button type="submit" disabled={busy || !code.trim()}>
            {busy ? 'Updating…' : 'Update tracking'}
          </button>
        </form>

        {msg && <p className={`pkg-scan-msg${lastPkg ? ' ok' : msg.startsWith('Scanned:') ? '' : ' err'}`}>{msg}</p>}

        {lastPkg && (
          <div className="pkg-scan-last">
            <Package size={18} />
            <span>
              HBL {lastPkg.parentHblNo || lastPkg.collectionNo} · SUB {lastPkg.subNo}
            </span>
          </div>
        )}

        <p className="pkg-scan-link">
          <Link to="/track-package">View package tracking page</Link>
        </p>
      </div>
    </div>
  );
}
